/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: utils/memberHelpers.ts (Ring Groups)
 🗓️ Created: 2025-01-15
 🧩 Description:
    Helper utilities for managing Ring Group members in forms.

────────────────────────────────────────────────────────────────*/

import { MemberRow } from '../types';

/**
 * Resequence member rows with sequential order indices
 */
export function resequenceMemberRows(rows: MemberRow[]): MemberRow[] {
  return rows.map((row, index) => {
    const newOrderIndex = String(index + 1);
    // Only create new object if orderIndex actually changed
    if (row.orderIndex === newOrderIndex) {
      return row;
    }
    return { ...row, orderIndex: newOrderIndex };
  });
}

/**
 * Normalize member rows for API submission
 */
export function normalizeMemberRows(rows: MemberRow[], ringTimeoutFallback: number) {
  return rows
    .map((row, index) => {
      const extension = row.extension.trim();
      if (!extension) {
        return null;
      }

      const delay = Number(row.delay);
      const timeout = Number(row.timeout);

      // Include id only if it exists (for updates), omit for new members (inserts)
      const memberData: {
        extension: string;
        orderIndex: number;
        delay: number;
        timeout: number;
        prompt: string | null;
        description: string | null;
        enabled: boolean;
        id?: number;
      } = {
        extension,
        orderIndex: index + 1,
        delay: Number.isFinite(delay) && delay >= 0 ? Math.trunc(delay) : 0,
        timeout: Number.isFinite(timeout) && timeout > 0 ? Math.trunc(timeout) : ringTimeoutFallback,
        prompt: row.prompt.trim() || null,
        description: row.description.trim() || null,
        enabled: Boolean(row.enabled)
      };

      // Only include id if it exists (not undefined, not null, and is a positive number)
      if (row.id !== undefined && row.id !== null && typeof row.id === 'number' && row.id > 0) {
        memberData.id = row.id;
      }

      return memberData;
    })
    .filter(
      (
        member
      ): member is {
        extension: string;
        orderIndex: number;
        delay: number;
        timeout: number;
        prompt: string | null;
        description: string | null;
        enabled: boolean;
        id?: number;
      } => member !== null
    );
}

/**
 * Compare if two member arrays are effectively the same
 */
export function areMembersEqual(a: MemberRow[], b: MemberRow[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    const memberA = a[i];
    const memberB = b[i];
    if (
      memberA.id !== memberB.id ||
      memberA.extension !== memberB.extension ||
      memberA.orderIndex !== memberB.orderIndex ||
      memberA.delay !== memberB.delay ||
      memberA.timeout !== memberB.timeout ||
      memberA.prompt !== memberB.prompt ||
      memberA.description !== memberB.description ||
      memberA.enabled !== memberB.enabled ||
      (memberA as any).__tempId !== (memberB as any).__tempId
    ) {
      return false;
    }
  }
  return true;
}

